/**
 * Confirm-and-delete modal for a single dealer plate. Plate deletion is
 * low-frequency and high-consequence (the worker won't have a plate to
 * paste once the operator deletes a default and forgets to mark another),
 * so the confirm is a modal rather than the usual confirm-undo toast.
 */
import React, { useState } from "react"

import { THEME } from "../../config/theme"
import { DealerPlate, deleteDealerPlate } from "../../api/dealerPlates"

interface Props {
  dealerGroupId: number
  plate: DealerPlate
  onClose: () => void
  onDeleted: () => void
}

export default function DeletePlateConfirm({
  dealerGroupId, plate, onClose, onDeleted,
}: Props) {
  const [deleting, setDeleting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleDelete = async () => {
    setDeleting(true)
    setError(null)
    try {
      await deleteDealerPlate(dealerGroupId, plate.id)
      onDeleted()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed")
      setDeleting(false)
    }
  }

  return (
    <div style={modalOverlayStyle} onClick={deleting ? undefined : onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={modalTitleStyle}>Delete this plate?</h2>
        <p style={bodyTextStyle}>
          {plate.is_default ? (
            <>
              <strong>{plate.name}</strong> is the default plate for this dealer.
              {" "}Deleting it leaves the dealer with no default until you mark
              another plate. This cannot be undone.
            </>
          ) : (
            <>
              <strong>{plate.name}</strong> will be removed from this dealer.
              This cannot be undone.
            </>
          )}
        </p>

        {error && <div style={errorBannerStyle}>{error}</div>}

        <div style={actionsRowStyle}>
          <button
            onClick={onClose}
            disabled={deleting}
            style={secondaryButtonStyle}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={dangerButtonStyle}
          >
            {deleting ? "Deleting…" : "Delete Plate"}
          </button>
        </div>
      </div>
    </div>
  )
}


// Mirrors modalOverlayStyle / modalStyle / dangerButtonStyle from
// PhotoConfigurationPage.tsx so the visual rhythm matches the other
// modals on the page.
const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}

const modalStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  width: "min(440px, 92vw)",
  padding: THEME.spacing.lg,
  fontFamily: THEME.fonts.primary,
}

const modalTitleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
  marginBottom: THEME.spacing.md,
}

const bodyTextStyle: React.CSSProperties = {
  fontSize: 14,
  color: THEME.colors.neutral.light.textPrimary,
  lineHeight: 1.5,
  marginTop: 0,
  marginBottom: THEME.spacing.md,
}

const errorBannerStyle: React.CSSProperties = {
  background: THEME.colors.danger,
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  color: THEME.colors.semantic.danger,
  fontSize: 13,
  marginBottom: THEME.spacing.md,
}

const actionsRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: THEME.spacing.sm,
  marginTop: THEME.spacing.md,
}

const secondaryButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

const dangerButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  background: THEME.colors.semantic.danger,
  color: THEME.colors.white,
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}
